import { useState } from 'react';
import { DEFAULT_REACTION_PRESETS, DEFAULT_SOUNDBITE_PRESETS, SOUNDBITE_PACKS, isSoundbiteOwned } from '@flip7/shared';
import { useAuth } from '../contexts/AuthContext';
import { SortableList } from './SortableList';

const API_BASE = import.meta.env.VITE_API_URL || '';

const soundbiteEntries = Object.entries(DEFAULT_SOUNDBITE_PRESETS);

export function ReactionsPanel() {
  const { accessToken, user, refreshUser } = useAuth();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<{ id: string; icon: string; label: string }[]>([]);
  const [saving, setSaving] = useState(false);
  const [savingSoundbites, setSavingSoundbites] = useState(false);

  if (!accessToken) {
    return (
      <div className="social-empty">
        <p className="social-empty__text">Sign in to customize reactions.</p>
      </div>
    );
  }

  const defaults = Object.entries(DEFAULT_REACTION_PRESETS).map(([id, p]) => ({ id, icon: p.icon, label: p.label }));
  const favList = user?.favoriteSoundbites ?? [];
  const favSet = new Set(favList);

  const save = async () => {
    if (!accessToken) return;
    setSaving(true);
    await fetch(`${API_BASE}/api/users/me/reactions`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
      body: JSON.stringify({ reactions: draft }),
    });
    await refreshUser();
    setSaving(false);
    setEditing(false);
  };

  const saveFavorites = async (ids: string[]) => {
    setSavingSoundbites(true);
    await fetch(`${API_BASE}/api/users/me/favorite-soundbites`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
      body: JSON.stringify({ soundbiteIds: ids }),
    });
    await refreshUser();
    setSavingSoundbites(false);
  };

  const toggleFavorite = (id: string) => {
    const next = [...favList];
    const idx = next.indexOf(id);
    if (idx >= 0) next.splice(idx, 1);
    else next.push(id);
    saveFavorites(next);
  };

  return (
    <div className="reactions-panel">
      {/* ── Reactions card ── */}
      <div className="ornate-border ornate-border--compact">
        <div className="reactions-panel__header">
          <p className="drawer-title">Reactions</p>
          {!editing ? (
            <button
              onClick={() => { setDraft(user?.customReactions ?? defaults); setEditing(true); }}
              className="btn btn-secondary btn-small"
              style={{ fontSize: '0.6rem', padding: '4px 10px', minHeight: 'unset' }}
            >
              Edit
            </button>
          ) : (
            <div className="flex-row gap-sm">
              <button onClick={() => setEditing(false)} className="btn btn-secondary btn-small" style={{ fontSize: '0.6rem', padding: '4px 10px', minHeight: 'unset' }}>
                Cancel
              </button>
              <button onClick={save} disabled={saving} className="btn btn-primary btn-small" style={{ fontSize: '0.6rem', padding: '4px 10px', minHeight: 'unset' }}>
                {saving ? '...' : 'Save'}
              </button>
            </div>
          )}
        </div>

        {editing ? (
          <div className="reactions-editor">
            <SortableList
              items={draft}
              keyFn={(_, i) => `r-${i}`}
              onReorder={setDraft}
              renderItem={(r, i) => (
                <div className="reactions-editor__row">
                  <input
                    type="text"
                    value={r.icon}
                    onChange={e => {
                      const updated = [...draft];
                      updated[i] = { ...updated[i], icon: e.target.value };
                      setDraft(updated);
                    }}
                    className="reactions-editor__icon-input"
                    maxLength={4}
                    aria-label="Emoji"
                  />
                  <input
                    type="text"
                    value={r.label}
                    onChange={e => {
                      const updated = [...draft];
                      updated[i] = { ...updated[i], label: e.target.value, id: e.target.value.toLowerCase().replace(/[^a-z0-9]/g, '') };
                      setDraft(updated);
                    }}
                    className="reactions-editor__label-input"
                    maxLength={30}
                    placeholder="Label"
                    aria-label="Label"
                  />
                  <button onClick={() => setDraft(draft.filter((_, j) => j !== i))} className="reactions-editor__remove" aria-label="Remove">
                    &times;
                  </button>
                </div>
              )}
            />
            <div className="reactions-editor__actions">
              {draft.length < 100 && (
                <button
                  onClick={() => setDraft([...draft, { id: `custom_${Date.now()}`, icon: '\uD83D\uDE00', label: 'New' }])}
                  className="reactions-editor__add"
                >
                  + Add
                </button>
              )}
              <button onClick={() => setDraft(defaults)} className="reactions-panel__link reactions-panel__link--muted reactions-panel__link--reset">
                Reset
              </button>
            </div>
          </div>
        ) : (
          <div className="reactions-preview">
            {(user?.customReactions ?? defaults).map(r => (
              <span key={r.id} className="reactions-preview__item">
                {r.icon} {r.label}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* ── Soundbites card ── */}
      <div className="ornate-border ornate-border--compact">
        <div className="reactions-panel__header">
          <p className="drawer-title">Soundbites</p>
          <span style={{ fontSize: '0.55rem', color: 'var(--text-muted)', fontWeight: 600 }}>
            {favSet.size > 0 ? `${favSet.size} in game bar` : 'Tap to pick'}
          </span>
        </div>

        {/* Favorited — draggable order */}
        {favList.length > 0 && (
          <div style={{ marginBottom: 10 }}>
            <p className="drawer-section-label">Your bar order</p>
            <SortableList
              items={favList}
              keyFn={id => `fav-${id}`}
              onReorder={saveFavorites}
              className="soundbite-favorites"
              renderItem={id => {
                const preset = DEFAULT_SOUNDBITE_PRESETS[id];
                if (!preset) return null;
                return (
                  <button
                    className="soundbite-fav soundbite-fav--active"
                    onClick={() => toggleFavorite(id)}
                    disabled={savingSoundbites}
                    style={{ width: '100%' }}
                  >
                    <span>{preset.icon}</span>
                    <span>{preset.label}</span>
                  </button>
                );
              }}
            />
            <div className="gold-divider" style={{ margin: '10px auto 6px', width: '40px' }} />
          </div>
        )}

        {/* All packs */}
        {SOUNDBITE_PACKS.map(pack => {
          const packEntries = soundbiteEntries.filter(([, p]) => p.pack === pack);
          if (packEntries.length === 0) return null;
          return (
            <div key={pack} style={{ marginBottom: 10 }}>
              <p className="drawer-section-label">{pack}</p>
              <div className="soundbite-favorites">
                {packEntries.map(([id, preset]) => {
                  const isOwned = isSoundbiteOwned(id, preset, user?.inventory ?? [], user?.achievements);
                  const isFav = favSet.has(id);
                  return (
                    <button
                      key={id}
                      className={`soundbite-fav${isFav ? ' soundbite-fav--active' : ''}${!isOwned ? ' soundbite-fav--locked' : ''}`}
                      onClick={() => isOwned && toggleFavorite(id)}
                      disabled={savingSoundbites || !isOwned}
                    >
                      <span>{isOwned ? preset.icon : '\uD83D\uDD12'}</span>
                      <span>{preset.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
